// TaxChecklist.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  ProgressBar,
  Row,
  Stack
} from "react-bootstrap";

type ChecklistItem = {
  id: string;
  label: string;
  checked?: boolean;
};

type ChecklistSection = {
  id: string;
  title: string;
  items: ChecklistItem[];
};

export type TaxChecklistProps = {
  title?: string;
  storageKey?: string; // localStorage key
  defaultOpenAll?: boolean;
};

const DEFAULT_STORAGE_KEY = "Tax_Checklist_W2_401k_Investments_v1";

const initialSections: ChecklistSection[] = [
  {
    id: "personal",
    title: "1. Personal Information",
    items: [
      { id: "pi-1", label: "Full name, DOB, SSN (you, spouse, dependents)" },
      { id: "pi-2", label: "Current address and phone number" },
      { id: "pi-3", label: "Direct deposit info (routing & account)" }
    ]
  },
  {
    id: "income",
    title: "2. Income Documents",
    items: [
      { id: "inc-1", label: "Form W-2 from employer(s)" },
      { id: "inc-2", label: "Form 1099-INT – bank interest" },
      { id: "inc-3", label: "Form 1099-DIV – dividends (stocks/mutual funds/ETFs)" },
      { id: "inc-4", label: "Form 1099-B – investment sales (capital gains/losses)" },
      { id: "inc-5", label: "Form 1099-R – retirement distributions/rollovers" },
      { id: "inc-6", label: "Form 5498 – IRA contribution confirmation" },
      { id: "inc-7", label: "Form 1099-NEC or 1099-K – side/freelance income" },
      { id: "inc-8", label: "Form 1099-G – unemployment" },
      { id: "inc-9", label: "Form 1099-SA – HSA distributions" }
    ]
  },
  {
    id: "retirement",
    title: "3. Retirement and 401(k) Information",
    items: [
      { id: "ret-1", label: "401(k) contributions (W-2 Box 12)" },
      { id: "ret-2", label: "Employer match details (for records)" },
      { id: "ret-3", label: "Traditional or Roth IRA contributions" },
      { id: "ret-4", label: "Form 5498-SA – HSA contributions" }
    ]
  },
  {
    id: "invest",
    title: "4. Investment and Brokerage Records",
    items: [
      { id: "inv-1", label: "Annual brokerage statements (Fidelity/Schwab/Vanguard/etc.)" },
      { id: "inv-2", label: "Crypto transaction summaries (if applicable)" },
      { id: "inv-3", label: "Dividend reinvestment & cost basis records" },
      { id: "inv-4", label: "Capital gain/loss summary report (from broker)" }
    ]
  },
  {
    id: "health",
    title: "5. Health and Insurance Information",
    items: [
      { id: "hlth-1", label: "Form 1095-A – Marketplace coverage" },
      { id: "hlth-2", label: "Form 1095-B or 1095-C – Employer coverage" },
      { id: "hlth-3", label: "HSA/FSA contribution & distribution records" },
      { id: "hlth-4", label: "Out-of-pocket medical/dental expenses (if itemizing)" }
    ]
  },
  {
    id: "deductions",
    title: "6. Deductions and Credits",
    items: [
      { id: "ded-1", label: "Form 1098 – mortgage interest" },
      { id: "ded-2", label: "Property tax records" },
      { id: "ded-3", label: "Charitable donation receipts (cash or goods)" },
      { id: "ded-4", label: "Form 1098-E – student loan interest" },
      { id: "ded-5", label: "Form 1098-T – education expenses" },
      { id: "ded-6", label: "State & local income taxes paid (or sales tax if applicable)" },
      { id: "ded-7", label: "Large medical expenses (if itemizing)" },
      { id: "ded-8", label: "Child Tax Credit / Dependent Care Credit (if applicable)" },
      { id: "ded-9", label: "Education credits – AOTC or LLC" },
      { id: "ded-10", label: "Energy credits (solar, EV charger, etc.)" }
    ]
  },
  {
    id: "adjustments",
    title: "7. Adjustments and Other Items",
    items: [
      { id: "adj-1", label: "Moving expenses (active-duty military only)" },
      { id: "adj-2", label: "Educator expenses (if eligible)" },
      { id: "adj-3", label: "HSA, IRA, or 529 plan contributions" },
      { id: "adj-4", label: "Student loan payments – interest portion" }
    ]
  },
  {
    id: "estimates",
    title: "8. Estimated Taxes and Withholding",
    items: [
      { id: "est-1", label: "Record of quarterly estimated payments (if any)" },
      { id: "est-2", label: "Prior year tax return (carryovers, references)" },
      { id: "est-3", label: "Verify withholding (IRS Withholding Estimator)" }
    ]
  },
  {
    id: "eoy",
    title: "9. End-of-Year Review",
    items: [
      { id: "eoy-1", label: "Compare investment income vs. capital gains distributions" },
      { id: "eoy-2", label: "Max out 401(k) / IRA contributions (deadline aware)" },
      { id: "eoy-3", label: "Review Saver’s Credit eligibility" },
      { id: "eoy-4", label: "Consider tax-loss harvesting if applicable" }
    ]
  },
  {
    id: "org",
    title: "10. Organizational Tips",
    items: [
      { id: "org-1", label: "Keep forms in one labeled (digital/physical) folder" },
      { id: "org-2", label: "Save tax software login or CPA contact" },
      { id: "org-3", label: "Retain filed return & docs for at least 3 years" }
    ]
  }
];

function usePersistentSections(
  storageKey: string,
  seed: ChecklistSection[]
): [ChecklistSection[], React.Dispatch<React.SetStateAction<ChecklistSection[]>>] {
  const [sections, setSections] = useState<ChecklistSection[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw) as ChecklistSection[];
    } catch {}
    // default: ensure checked = false for all items
    return seed.map(s => ({
      ...s,
      items: s.items.map(i => ({ ...i, checked: false }))
    }));
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(sections));
    } catch {}
  }, [storageKey, sections]);

  return [sections, setSections];
}

const highlightMatch = (text: string, query: string) => {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

export const TaxChecklist: React.FC<TaxChecklistProps> = ({
  title = "Tax Preparation Checklist (W-2, 401(k), Investments)",
  storageKey = DEFAULT_STORAGE_KEY,
  defaultOpenAll = false
}) => {
  const [sections, setSections] = usePersistentSections(storageKey, initialSections);
  const [search, setSearch] = useState("");
  const [openKeys, setOpenKeys] = useState<string[]>(
    defaultOpenAll ? sections.map(s => s.id) : [sections[0]?.id].filter(Boolean)
  );

  // Progress
  const { total, checked } = useMemo(() => {
    const t = sections.reduce((sum, s) => sum + s.items.length, 0);
    const c = sections.reduce((sum, s) => sum + s.items.filter(i => i.checked).length, 0);
    return { total: t, checked: c };
  }, [sections]);
  const pct = total ? Math.round((checked / total) * 100) : 0;

  // Search filter
  const filtered = useMemo(() => {
    if (!search.trim()) return sections;
    const q = search.trim().toLowerCase();
    return sections
      .map(s => {
        const items = s.items.filter(i => i.label.toLowerCase().includes(q));
        if (items.length === 0) return null;
        return { ...s, items };
      })
      .filter(Boolean) as ChecklistSection[];
  }, [sections, search]);

  const toggleItem = (sectionId: string, itemId: string) => {
    setSections(prev =>
      prev.map(s =>
        s.id !== sectionId
          ? s
          : {
              ...s,
              items: s.items.map(i => (i.id === itemId ? { ...i, checked: !i.checked } : i))
            }
      )
    );
  };

  const setSectionAll = (sectionId: string, value: boolean) => {
    setSections(prev =>
      prev.map(s =>
        s.id !== sectionId ? s : { ...s, items: s.items.map(i => ({ ...i, checked: value })) }
      )
    );
  };

  const resetAll = () => {
    if (!window.confirm("Clear all checkmarks for this checklist?")) return;
    setSections(prev => prev.map(s => ({ ...s, items: s.items.map(i => ({ ...i, checked: false })) })));
  };

  const checkAll = () => {
    setSections(prev => prev.map(s => ({ ...s, items: s.items.map(i => ({ ...i, checked: true })) })));
  };

  const openAll = () => setOpenKeys(sections.map(s => s.id));
  const closeAll = () => setOpenKeys([]);

//   const onAccordionSelect = (eventKey: string | null) => {
//     if (!eventKey) return;
//     setOpenKeys(keys =>
//       keys.includes(eventKey) ? keys.filter(k => k !== eventKey) : [...keys, eventKey]
//     );
//   };

// Replace the old onAccordionSelect with this:
        // const onAccordionSelect = (
        // eventKey: string | number | null | undefined,
        // _e?: any
        // ) => {
        // if (eventKey == null) return; // handles null/undefined
        // const key = String(eventKey); // normalize number -> string

        // setOpenKeys((keys) =>
        //     keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
        // );
        // };
  // ✅ Replace your handler with this one
  const onAccordionSelect: (eventKey: any, _e?: any) => void = (eventKey) => {
    if (eventKey == null) return;

    // When alwaysOpen is true, react-bootstrap may pass an array of keys.
    // Normalize to the latest/last key as a string.
    const keysArray = Array.isArray(eventKey) ? eventKey : [eventKey];
    const lastKey = String(keysArray[keysArray.length - 1]);

    setOpenKeys((prev) =>
      prev.includes(lastKey) ? prev.filter((k) => k !== lastKey) : [...prev, lastKey]
    );
  };


  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(sections, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tax_checklist_progress.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container className="py-4">
      <Row className="mb-3">
        <Col>
          <h2 className="mb-1">{title}</h2>
          <div className="text-muted">
            Progress: <strong>{checked}</strong> / {total} items
          </div>
          <ProgressBar className="mt-2" now={pct} label={`${pct}%`} />
        </Col>
      </Row>

      <Row className="align-items-center g-2 mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>Search</InputGroup.Text>
            <Form.Control
              placeholder="Type to filter items…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search checklist"
            />
            {search && (
              <Button variant="outline-secondary" onClick={() => setSearch("")}>
                Clear
              </Button>
            )}
          </InputGroup>
        </Col>
        <Col md="auto">
          <Stack direction="horizontal" gap={2}>
            <Button variant="outline-primary" onClick={openAll}>Open All</Button>
            <Button variant="outline-secondary" onClick={closeAll}>Close All</Button>
            <Button variant="outline-success" onClick={checkAll}>Check All</Button>
            <Button variant="outline-danger" onClick={resetAll}>Clear All</Button>
            <Button variant="outline-dark" onClick={exportJSON}>Export JSON</Button>
          </Stack>
        </Col>
      </Row>

      <Accordion activeKey={openKeys} alwaysOpen onSelect={onAccordionSelect}>
        {filtered.map((section) => {
          const sectionTotal = section.items.length;
          const sectionChecked = section.items.filter(i => i.checked).length;
          const secPct = sectionTotal ? Math.round((sectionChecked / sectionTotal) * 100) : 0;

          return (
            <Accordion.Item eventKey={section.id} key={section.id}>
              <Accordion.Header>
                <Stack direction="horizontal" className="w-100" gap={3}>
                  <span className="me-auto">{section.title}</span>
                  <Badge bg={secPct === 100 ? "success" : secPct > 0 ? "info" : "secondary"}>
                    {sectionChecked}/{sectionTotal}
                  </Badge>
                </Stack>
              </Accordion.Header>
              <Accordion.Body>
                <Card className="mb-3">
                  <Card.Body className="d-flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline-success"
                      onClick={() => setSectionAll(section.id, true)}
                    >
                      Check Section
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => setSectionAll(section.id, false)}
                    >
                      Clear Section
                    </Button>
                  </Card.Body>
                </Card>

                <Stack gap={2}>
                  {section.items.map(item => (
                    <Form.Check
                      key={item.id}
                      type="checkbox"
                      id={item.id}
                      label={highlightMatch(item.label, search)}
                      checked={!!item.checked}
                      onChange={() => toggleItem(section.id, item.id)}
                    />
                  ))}
                  {section.items.length === 0 && (
                    <div className="text-muted fst-italic">No matches in this section.</div>
                  )}
                </Stack>
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-muted p-3">No items match your search.</div>
        )}
      </Accordion>

      <Row className="pt-4">
        <Col>
          <div className="text-muted small">
            Tip: Your progress is saved locally in your browser (key: <code>{storageKey}</code>).
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default TaxChecklist;
